import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Column, Row, usePagination, useSortBy, useTable } from 'react-table';

import ClusterApi from '../../api/ClusterApi';
import ReactTable from '../../component/ReactTable';
import { SEVERITY_CLASS_NAMES } from '../../component/Constants';
import { TimeRangeContext } from '../../context/TimeRangeContext';
import { buildRedirectQuery } from '../../helpers/timeRangeUtils';
import { formatBytes, getDefaultPageSize } from '../../helpers/utils';
import InstanceAvailabilityGroup from '../../types/cluster/InstanceAvailabilityGroup';
import '../../component/cluster/instanceAgBadge.scss';
import BackupChainStatus from './BackupChainStatus';
import { CHAIN_STATUS_RANK, ChainBackup, chainBackup, chainDifferential } from './backupChainUtils';
import {
    BACKUP_FLAG_TEXT,
    BACKUP_KIND_LABELS,
    BackupKind,
    ChainRow,
    backupFlag,
    backupWarningTooltip,
    formatBackupDuration,
    formatBackupTime,
    formatInterval,
} from './backupUtils';

interface Props {
    rows: ChainRow[];
    loading: boolean;
    selected: ChainRow | null;
    onSelect: (row: ChainRow) => void;
}

// Most serious first, for sorting the flag column.
const FLAG_RANK = { failed: 5, broken: 4, never: 3, nolog: 2, chainwarning: 1 };

const FLAG_ICONS = {
    failed: 'fas fa-circle-xmark red-icon',
    broken: 'fas fa-link-slash red-icon',
    never: 'fas fa-triangle-exclamation amber-icon',
    nolog: 'fas fa-triangle-exclamation amber-icon',
    chainwarning: 'fas fa-triangle-exclamation amber-icon',
};

function latest(row: ChainRow, kind: BackupKind): ChainBackup {
    return kind === 'differential' ? chainDifferential(row) : chainBackup(row, kind);
}

// One column group per kind of backup, as Start date / Duration / Size.
function kindColumns(kind: BackupKind): Column<ChainRow> {
    const time = (row: ChainRow) => {
        const start = latest(row, kind).start;
        return start ? Date.parse(start) : 0;
    };
    return {
        Header: BACKUP_KIND_LABELS[kind],
        id: kind,
        headerClassName: `backups__group backups__group--${kind}`,
        columns: [
            {
                Header: 'Start date',
                id: `${kind}start`,
                accessor: time,
                sortType: 'basic',
                Cell: ({ row }: { row: Row<ChainRow> }) => {
                    const backup = latest(row.original, kind);
                    // Only named when a replica other than this row's took it.
                    const other = backup.source && backup.source !== row.original.name;
                    return <span data-tooltip-id="global-tooltip"
                                 data-tooltip-content={other ? `Taken on ${backup.source}` : undefined}>
                        {formatBackupTime(backup.start)}
                        {other && <i className="fal fa-share-nodes ms-1 text-muted" role="img"
                                     aria-label={`Taken on ${backup.source}`}/>}
                    </span>;
                },
            },
            {
                Header: 'Duration',
                id: `${kind}duration`,
                accessor: row => latest(row, kind).duration ?? -1,
                sortType: 'basic',
                className: 'text-end',
                Cell: ({ row }: { row: Row<ChainRow> }) => {
                    const backup = latest(row.original, kind);
                    return backup.start ? formatBackupDuration(backup.duration) : '';
                },
            },
            {
                Header: 'Size',
                id: `${kind}size`,
                accessor: row => latest(row, kind).size ?? -1,
                sortType: 'basic',
                className: 'text-end',
                Cell: ({ row }: { row: Row<ChainRow> }) => {
                    const backup = latest(row.original, kind);
                    return backup.start ? formatBytes(backup.size) : '';
                },
            },
        ],
    } as Column<ChainRow>;
}

// A database with the availability group its chip links to, once looked up.
type LinkedRow = ChainRow & { group?: InstanceAvailabilityGroup };

// A click on a chip follows its link rather than opening the row's history.
const stopRowClick = (event: React.MouseEvent) => event.stopPropagation();

function BackupChainTable(props: Props) {
    const { timeRange } = useContext(TimeRangeContext);
    const [memberships, setMemberships] = useState<Map<number, InstanceAvailabilityGroup[]>>(new Map());
    const hasGroups = props.rows.some(row => row.replicasetname);

    // The summary names the availability group but not its id, which the link
    // needs, so it is looked up in the topology.
    useEffect(() => {
        if (!hasGroups) {
            return;
        }
        let active = true;
        ClusterApi.getMemberships().then(result => {
            if (active) {
                setMemberships(result);
            }
        });
        return () => {
            active = false;
        };
    }, [hasGroups]);

    // Joined into the data rather than the columns, so that the lookup landing
    // re-renders the cells instead of remounting them.
    const data = useMemo<LinkedRow[]>(() => props.rows.map(row => ({
        ...row,
        group: row.replicasetname ? row.replicas
            .flatMap(replica => memberships.get(replica.id) ?? [])
            .find(group => group.groupname === row.replicasetname) : undefined,
    })), [props.rows, memberships]);

    const columns = useMemo<Column<LinkedRow>[]>(() => {
        // The context default carries no time range, so the link drops the period
        // rather than formatting an undefined date.
        const search = timeRange?.from ? `?${buildRedirectQuery(timeRange)}` : '';

        return [
        {
            Header: 'Database',
            accessor: 'database',
            Cell: ({ row }: { row: Row<LinkedRow> }) => {
                const summary = row.original;
                const group = summary.group;
                return <div className="backups__database">
                    <div>
                        <i className="fas fa-database fa-fw me-1" aria-hidden="true"/>
                        <span className="fw-semibold">{summary.database}</span>
                    </div>
                    <div className="backups__chips">
                        {summary.replicasetname && (group
                            ? <Link className="ag-badge" onClick={stopRowClick}
                                    to={`/clusters/${group.clusterid}/availability-groups/${group.groupid}${search}`}
                                    data-tooltip-id="global-tooltip"
                                    data-tooltip-content={`Always On availability group ${group.groupname}`}>
                                <span className="ag-badge-role unknown">AG</span>
                                <span className="ag-badge-name">{summary.replicasetname}</span>
                            </Link>
                            : <span className="ag-badge">
                                <span className="ag-badge-role unknown">AG</span>
                                <span className="ag-badge-name text-body">{summary.replicasetname}</span>
                            </span>)}
                        {summary.replicas.map(replica => (
                            <Link key={replica.id} className="ag-badge" onClick={stopRowClick}
                                  to={`/instances/${replica.id}/activity${search}`}
                                  data-tooltip-id="global-tooltip"
                                  data-tooltip-content={replica.replicarole === 'primary'
                                      ? `${replica.name} holds the primary replica` : replica.name}>
                                {replica.replicarole === 'primary' &&
                                    <span className="ag-badge-role primary">P</span>}
                                <span className="ag-badge-name">
                                    {replica.name}
                                    {replica.state && replica.state !== 'ONLINE' &&
                                        <span className="badge bg-secondary">{replica.state}</span>}
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>;
            },
        },
        {
            Header: <i className="fas fa-triangle-exclamation" role="img" aria-label="Backup warning"
                       data-tooltip-id="global-tooltip" data-tooltip-content="Backup warning"/>,
            id: 'flag',
            accessor: row => {
                const flag = backupFlag(row);
                return flag ? FLAG_RANK[flag] : 0;
            },
            sortType: 'basic',
            sortDescFirst: true,
            className: 'backups__icon-column',
            Cell: ({ row }: { row: Row<ChainRow> }) => {
                const summary = row.original;
                const flag = backupFlag(summary);
                return flag ? <i className={FLAG_ICONS[flag]} role="img"
                                 aria-label={BACKUP_FLAG_TEXT[flag]}
                                 data-tooltip-id="global-tooltip"
                                 data-tooltip-class-name="backups-warning-tooltip"
                                 data-tooltip-content={backupWarningTooltip(summary)}/> : null;
            },
        },
        {
            Header: <i className="fas fa-bell" role="img" aria-label="Backup alert"
                       data-tooltip-id="global-tooltip" data-tooltip-content="Backup alert"/>,
            id: 'alert',
            // Severity 1 is critical, so invert it to sort the worst first.
            accessor: row => row.alertseverity ? 4 - row.alertseverity : 0,
            sortType: 'basic',
            sortDescFirst: true,
            className: 'backups__icon-column',
            Cell: ({ row }: { row: Row<ChainRow> }) => row.original.alertseverity ? <i
                className={`fas fa-bell ${SEVERITY_CLASS_NAMES[row.original.alertseverity] || ''}`}
                role="img" aria-label="Backup alert raised"
                data-tooltip-id="global-tooltip" data-tooltip-content="Backup alert raised"/> : null,
        },
        kindColumns('full'),
        kindColumns('differential'),
        kindColumns('log'),
        {
            Header: 'Recovery model',
            accessor: row => row.recoverymodel || '',
            id: 'recoverymodel',
        },
        {
            Header: 'Log chain',
            id: 'chainstatus',
            accessor: row => row.chainstatus ? CHAIN_STATUS_RANK[row.chainstatus] : 0,
            sortType: 'basic',
            sortDescFirst: true,
            Cell: ({ row }: { row: Row<ChainRow> }) => <BackupChainStatus row={row.original}/>,
        },
        {
            Header: 'Worst RPO',
            id: 'worstrpo',
            accessor: row => row.worstrpo ?? -1,
            sortType: 'basic',
            className: 'text-end',
            Cell: ({ row }: { row: Row<ChainRow> }) => formatInterval(row.original.worstrpo),
        },
        ];
    }, [timeRange]);

    const tableInstance = useTable<LinkedRow>({
            columns,
            data,
            initialState: { pageSize: getDefaultPageSize(), sortBy: [{ id: 'database', desc: false }] } as any,
            autoResetPage: false,
            autoResetSortBy: false,
        } as any,
        useSortBy, usePagination);

    const {
        getTableProps, getTableBodyProps, headerGroups, prepareRow, page, canPreviousPage, canNextPage,
        pageOptions, pageCount, gotoPage, nextPage, previousPage, setPageSize, state: { pageIndex, pageSize },
    } = tableInstance as any;

    const isSelected = (row: ChainRow) =>
        props.selected?.id === row.id && props.selected?.database === row.database;

    return <ReactTable<LinkedRow>
        getTableProps={getTableProps}
        getTableBodyProps={getTableBodyProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        page={page}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        pageOptions={pageOptions}
        pageCount={pageCount}
        gotoPage={gotoPage}
        nextPage={nextPage}
        previousPage={previousPage}
        setPageSize={setPageSize}
        pageIndex={pageIndex}
        pageSize={pageSize}
        dataLength={props.rows.length}
        loading={props.loading}
        getRowProps={(row) => ({
            className: `table-row backups__row${isSelected(row.original) ? ' selected' : ''}`,
            role: 'button',
            onClick: () => props.onSelect(row.original),
        })}
    />;
}

export default BackupChainTable;
